import React, { useEffect, MouseEventHandler, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import useUrl from 'hooks/useUrl';
import { Button, Box } from '@mui/material';
import useSandboxes, { SandboxIsoStateIds } from 'SandboxModule/hooks/useSandboxes';
import { Grid, Divider, Chip } from 'ProfileModule/components/styles';

import {
  About,
  Details,
  IntegrationList,
  Skills,
  Earnings,
  Elsewhere,
  Orders,
  Revenue,
  TagsCard,
} from 'ProfileModule/components';
import useAppNavigation from 'hooks/useAppNavigation';
import useNavigationContext from 'hooks/useNavigationContext';
import useUpdateRedux from 'hooks/useUpdateRedux';
import * as thunkActions from 'thunk';

import { selectIsDevMode } from 'selectors';

import { fetchTeamMembers } from 'ProfileModule/thunks';
import { MEMBER_TABLE_VIEWS, DEFAULT_MEMBER_TABLE_VIEW } from '@constants/members';
import { fetchIntegrations, fetchIntegrationHealth } from 'thunk';
import { integrationHealthIsoStateIds } from '@constants';
import {
  groupNonArchivedTargetServiceIds,
  integrationHealthIsoStateModifiers,
} from 'IntegrationModule/utils';
import { falsyCheckDate, formatIntegrationHealthDateParams, getTeamsV2PageUrl } from 'utils';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import useTeams from 'hooks/useTeams';
import useIntegrationHealthData from 'hooks/useIntegrationHealthData';
import * as constants from '@constants';
import { TeamInfoSectionContainer } from 'TeamModule/components/TeamsInfoSection/TeamInfoSectionContainer';
import { useProvisionOptionsMenu } from 'IntegrationModule/components/ProvisionModal/hooks/useProvisionOptionsMenu';
import { useTeamActionMenu } from 'TeamModule/components/TeamActionMenu/useTeamActionMenu';
import { useEnableDdiModal } from 'TeamModule/components/TeamActionMenu/useEnableDdiModal';
import {
  getTeamStatus,
  TeamStatus,
  TeamStatusBadge,
  SsoBadge,
} from 'TeamModule/components/TeamsV2Table/DataCells/TeamsV2StatusCell';
import StandardPageHeader from 'components/StandardPageHeader';
import { ChipsContainer } from 'components/TagsListSectionWithInput';
import { useRequestStatus } from 'RequestStatusModule/hooks/useRequestStatus';
import useSearchTeamsByFieldsQuery from 'TeamModule/query/hooks/useSearchTeamsByFieldsQuery';
import { TeamSearchItem } from 'TeamModule/api/searchTeamsByFieldsApi';
import useEnsureTeamContext from 'ProfileModule/hooks/useEnsureTeamContext';
import { useTeamMemberCategoryCountsQuery } from 'StatisticsModule/query/hooks/useTeamMemberCategoryCountsQuery';

const IconContainer = styled.div`
  text-align: right;
`;

const TeamOptionContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

type TeamDisplayValuesInput = {
  teamData: TeamSearchItem | undefined;
  selectedTeam: ReturnType<typeof useTeams>['currentTeam'];
  teamIdToUse: number;
  tenantToUse: string | undefined;
  realmIdToUse: string | undefined;
  getSandbox: ReturnType<typeof useSandboxes>['getSandbox'];
};

/**
 * Header/About-card display values, preferring the fast search API payload and
 * falling back to the Redux team record and the URL tenant/realm.
 */
const deriveTeamDisplayValues = ({
  teamData,
  selectedTeam,
  teamIdToUse,
  tenantToUse,
  realmIdToUse,
  getSandbox,
}: TeamDisplayValuesInput) => {
  const name = teamData?.name || selectedTeam?.name;

  const isTeamDeactivated = !!selectedTeam?.deactivated_at;

  const deactivateInfo =
    isTeamDeactivated && selectedTeam
      ? `Deactivated at ${falsyCheckDate(selectedTeam.deactivated_at)} by ${
          selectedTeam.deactivated_by_name
        }`
      : '';

  const pageHeader = `${name || ''} - ${teamIdToUse || ''}`;

  const teamRealm = teamData?.realmId ? getSandbox(teamData.realmId) : undefined;
  const teamRealmToDisplay = teamRealm
    ? `${teamRealm.team_name} (${teamRealm.realm_id})`
    : (teamData?.realmId ?? realmIdToUse);

  // Compute displayTenant and displayRealmId once to avoid redundant calculations
  // Prefer team data from API, fall back to URL params
  const displayTenant = teamData?.tenant ?? tenantToUse;
  const displayRealmId = teamData?.realmId ?? realmIdToUse;

  return {
    name,
    isTeamDeactivated,
    deactivateInfo,
    pageHeader,
    teamRealm,
    teamRealmToDisplay,
    displayTenant,
    displayRealmId,
  };
};

const devOnlyStyle = (isDevMode: boolean) => ({ display: isDevMode ? 'block' : 'none' });

/** Returns undefined (not an empty element) so the header can omit the suffix slot entirely. */
const renderTeamStatusSuffix = (teamStatus: TeamStatus | null, ssoEnabled?: boolean) =>
  teamStatus || ssoEnabled ? (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      {teamStatus && <TeamStatusBadge status={teamStatus} size="medium" />}
      {ssoEnabled && <SsoBadge size="medium" />}
    </Box>
  ) : undefined;

type TeamAboutCardPropsType = {
  selectedTeam: ReturnType<typeof useTeams>['currentTeam'];
  teamData: TeamSearchItem | undefined;
  tenant: string | undefined;
  realm: string | undefined;
  realmType?: string;
  teamId: number;
  internalContractorsCount?: number;
  isLoadingInternalContractors: boolean;
  isLoadingDetailedData: boolean;
  isLoadingBasicData: boolean;
  onEnableDdi?: () => void;
  isEnablingDdi?: boolean;
};

/** Spreads the search-API team payload onto the About card. */
const TeamAboutCard = ({
  selectedTeam,
  teamData,
  tenant,
  realm,
  realmType,
  teamId,
  internalContractorsCount,
  isLoadingInternalContractors,
  isLoadingDetailedData,
  isLoadingBasicData,
  onEnableDdi,
  isEnablingDdi,
}: TeamAboutCardPropsType) => (
  <About
    selectedTeam={selectedTeam}
    tenant={teamData?.tenant ?? tenant}
    realm={realm}
    realmType={realmType}
    teamId={teamId}
    uuid={teamData?.id}
    ssoEnabled={teamData?.ssoEnabled}
    openApiEnabled={teamData?.openApiEnabled}
    ddiEnabled={teamData?.ddiEnabled}
    onEnableDdi={onEnableDdi}
    isEnablingDdi={isEnablingDdi}
    internalTestTeam={teamData?.internalTestTeam}
    chelloCompliant={teamData?.chelloCompliant}
    aiEnabled={teamData?.aiEnabled}
    aiBlocked={teamData?.aiBlocked}
    nicknames={teamData?.nicknames}
    lastBackfilled={teamData?.lastBackfilled}
    scheduledArchiveDate={teamData?.scheduledArchiveDate}
    archivedAt={teamData?.archivedAt}
    reasonToArchive={teamData?.reasonToArchive}
    searchApiCsm={teamData?.csm}
    internalContractorsCount={internalContractorsCount}
    isLoadingInternalContractors={isLoadingInternalContractors}
    isLoadingDetailedData={isLoadingDetailedData}
    isLoadingBasicData={isLoadingBasicData}
  />
);

function Profile() {
  const { queryParams, pathParams, pushQueryParams } = useUrl();
  const { currentTeam, isFetchingTeams, currentTeamId, fetchTeams } = useTeams({
    shouldAutoLoad: false,
  });
  const selectedTeam = currentTeam;
  const teamIdToUse = +currentTeamId;
  const isDevMode = useAppSelector(selectIsDevMode);
  const { navigateToIntegrationDetailsPage } = useAppNavigation();

  const { getSandboxCredentialsWarningMessage, getSandbox, fetchSandboxes } = useSandboxes();
  const { shouldEnableIntegrationServerConnection } = useFeatureFlags();
  const warningMessage = getSandboxCredentialsWarningMessage({
    teamId: teamIdToUse,
  });

  // Fetch integration health data for displaying health status on integrations
  const { data: teamProfileIntegrationListIntegrationHealthData } = useIntegrationHealthData({
    isoStateId: constants.integrationHealthIsoStateIds.TeamProfileIntegrationList,
  });

  // Get navigation context (provides fallback to global state when URL params are missing)
  const navigationContext = useNavigationContext();

  // Read URL params for tenant/realm context
  const urlTenant = queryParams.get('tenant');
  const urlRealmId = queryParams.get('realmId');

  // URL params take priority, fall back to navigation context (global state)
  // This ensures breadcrumbs work even when navigating directly without URL params
  const tenantToUse = urlTenant ?? navigationContext.tenant;
  const realmIdToUse = urlRealmId ?? navigationContext.realmId;

  // Ensure the Rails session matches the URL tenant/realm before fetching data.
  // When accessing via direct URL (not from the team lookup table), the Rails session
  // may still be set to the user's default tenant, causing incomplete data.
  const { isContextReady } = useEnsureTeamContext({
    tenant: tenantToUse,
  });

  const dispatch = useAppDispatch();
  const { updateProfileReduxState } = useUpdateRedux();
  const fetchTeamMembersRequestStatusId = `fetchTeamMembers_${teamIdToUse}`;
  const { requestStatus: fetchTeamMembersRequestStatus } = useRequestStatus({
    requestStatusId: fetchTeamMembersRequestStatusId,
  });
  const isFetchingTeamMembers =
    (fetchTeamMembersRequestStatus && fetchTeamMembersRequestStatus.isRequesting) || false;

  const memberTableView = pathParams?.memberTableView;
  const currentView = memberTableView || DEFAULT_MEMBER_TABLE_VIEW;
  // Tabs that depend on the full team-members list and need it fetched here.
  // The All Members tab now self-fetches a single page (GLX-1740), so only the
  // Suppressed Emails tab still needs the entire list loaded into Redux.
  const tabNeedsTeamMembers = currentView === MEMBER_TABLE_VIEWS.SUPPRESSED_EMAILS;

  // Fetch team data (can be slow for large teams)
  // Gated on isContextReady to ensure the Rails session matches the URL tenant
  useEffect(() => {
    if (teamIdToUse && isContextReady) {
      fetchTeams({ teamIds: [teamIdToUse], tenant: tenantToUse, realmId: realmIdToUse });
      // Ensure the sandbox cache is populated so the About card can render the
      // realm's human-readable name. `useSandboxes.fetchSandboxes` overrides
      // the `meta` option internally, so tenant/realm scoping would be dropped
      // anyway; rely on the AppbarRealmSelector fetch populating the current
      // tenant's realms.
      fetchSandboxes(SandboxIsoStateIds.AppbarRealmSelector);
      updateProfileReduxState({
        agentToken: '',
        provisionIntegrationResponse: {},
        requestStatusesAndErrors: {
          [thunkActions.provisionQbd.typePrefix]: {
            error: {},
          },
        },
      });
    }
  }, [
    fetchTeams,
    fetchSandboxes,
    teamIdToUse,
    tenantToUse,
    realmIdToUse,
    updateProfileReduxState,
    isContextReady,
  ]);

  // Fetch team members on tabs that need the team-members list.
  useEffect(() => {
    if (teamIdToUse && tabNeedsTeamMembers && isContextReady) {
      dispatch(
        fetchTeamMembers({
          meta: { requestStatusId: fetchTeamMembersRequestStatusId },
          teamId: teamIdToUse,
          include_discarded: true,
          tenant: tenantToUse,
          realmId: realmIdToUse,
        }),
      );
    }
  }, [
    dispatch,
    fetchTeamMembersRequestStatusId,
    teamIdToUse,
    tenantToUse,
    realmIdToUse,
    tabNeedsTeamMembers,
    isContextReady,
  ]);

  // Fetch integration health data
  useEffect(() => {
    if (shouldEnableIntegrationServerConnection && teamIdToUse && isContextReady) {
      dispatch(
        fetchIntegrations({
          mosaicTeamId: teamIdToUse,
          includeArchived: true,
          tenant: tenantToUse,
          realmId: realmIdToUse,
          onSuccess: ({ response }) => {
            // Archived rows stay listed but are never health-checked (INTG-13144).
            const targetServiceToIdsHash = groupNonArchivedTargetServiceIds(response.data);

            Object.entries(targetServiceToIdsHash).forEach(([targetService, targetServiceIds]) => {
              dispatch(
                fetchIntegrationHealth({
                  targetService,
                  targetServiceIds,
                  serviceNames: [targetService],
                  startDate: formatIntegrationHealthDateParams(new Date()),
                  endDate: formatIntegrationHealthDateParams(new Date()),
                  isoStateId: integrationHealthIsoStateIds.TeamProfileIntegrationList,
                  modifier:
                    integrationHealthIsoStateModifiers[
                      integrationHealthIsoStateIds.TeamProfileIntegrationList
                    ],
                  tenant: tenantToUse,
                  realmId: realmIdToUse,
                }),
              );
            });
          },
        }),
      );
    }
  }, [
    dispatch,
    teamIdToUse,
    tenantToUse,
    realmIdToUse,
    isContextReady,
    shouldEnableIntegrationServerConnection,
  ]);

  const handleIntegrationNameClick = async (targetServiceId: number, targetServiceName: string) => {
    // Pass tenant/realmId to maintain context on the integration page
    const navigate = navigateToIntegrationDetailsPage(
      teamIdToUse,
      targetServiceName,
      targetServiceId,
      { tenant: tenantToUse, realmId: realmIdToUse },
    );
    navigate();
  };

  // Team Action menu
  const { openTeamActionMenu, TeamActionMenuComponent } = useTeamActionMenu();

  // Enable DDI button in the About card (GLX-1567)
  const { openEnableDdiModal, isEnablingDdi } = useEnableDdiModal();

  /* -------------------------------- Provision ------------------------------- */
  const { openProvisionOptionsMenu } = useProvisionOptionsMenu({
    teamId: teamIdToUse,
    tenant: tenantToUse,
    realmId: realmIdToUse,
    hideLegacyProvisionOptions: true,
  });

  const onProvisionButtonClick: MouseEventHandler<HTMLElement> = (e) => {
    openProvisionOptionsMenu({ target: e.currentTarget });
  };

  // Fetch team information from the API using tenant, realmId, and teamId for unique identification
  // This is a FAST API that should always respond quickly
  const { teamsSearch, isLoading: isLoadingTeamSearch } = useSearchTeamsByFieldsQuery({
    tenant: tenantToUse,
    realmId: realmIdToUse,
    teamId: teamIdToUse?.toString(),
    enabled: !!teamIdToUse && !!tenantToUse && !!realmIdToUse,
    limit: 1,
  });

  const teamData = useMemo(() => {
    return teamsSearch?.data?.[0];
  }, [teamsSearch?.data]);

  // Internal-contractor count is not part of the rails teams/all payload, so it
  // comes from the overall count statistics endpoint scoped to this team (GLX-1842).
  const { memberCategoryCounts, isLoadingMemberCategoryCounts } = useTeamMemberCategoryCountsQuery({
    teamId: teamIdToUse || undefined,
    tenant: tenantToUse,
    realmId: realmIdToUse,
    enabled: !!teamIdToUse && isContextReady,
  });

  // Get team status from navigation state (passed from TeamsV2Page) or compute from API data
  const location = useLocation();
  const locationState = location.state as { teamStatus?: TeamStatus } | null;

  const teamStatus = useMemo((): TeamStatus | null => {
    // First, try to use the status passed via navigation state (immediate feedback)
    if (locationState?.teamStatus) {
      return locationState.teamStatus;
    }
    // Fall back to computing status from API data (for direct URL access)
    if (teamData) {
      return getTeamStatus(teamData);
    }
    return null;
  }, [locationState, teamData]);

  // Enable button once fast API confirms team exists
  const disableActionButton = !teamIdToUse || !teamData;
  // keepPreviousData can leave teamData describing the previously viewed team,
  // and numeric team ids repeat across tenants/realms, so actions that write
  // from teamData only trust it once it identifies the exact team on screen.
  const isTeamDataCurrent =
    teamData?.teamId === String(teamIdToUse) &&
    teamData?.tenant === tenantToUse &&
    teamData?.realmId === realmIdToUse;

  const {
    name,
    isTeamDeactivated,
    deactivateInfo,
    pageHeader,
    teamRealm,
    teamRealmToDisplay,
    displayTenant,
    displayRealmId,
  } = deriveTeamDisplayValues({
    teamData,
    selectedTeam,
    teamIdToUse,
    tenantToUse,
    realmIdToUse,
    getSandbox,
  });

  // Sync tenant/realmId to URL query params so links can be shared
  useEffect(() => {
    const currentUrlTenant = queryParams.get('tenant') || '';
    const currentUrlRealmId = queryParams.get('realmId') || '';

    // Only update if we have values and they differ from current URL
    if (displayTenant && displayRealmId) {
      if (displayTenant !== currentUrlTenant || displayRealmId !== currentUrlRealmId) {
        pushQueryParams({ tenant: displayTenant, realmId: displayRealmId });
      }
    }
  }, [displayTenant, displayRealmId, queryParams, pushQueryParams]);

  // Build custom breadcrumbs showing: Home > Tenant > Realm > Team ID > Current View
  const viewLabel = (memberTableView || MEMBER_TABLE_VIEWS.ALL_MEMBERS)
    .split('_')
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  const customBreadcrumbs = useMemo(() => {
    const crumbs: Array<{ label: string; path?: string }> = [{ label: 'Home', path: '/' }];

    // Add Tenant if available - clickable to filter teams by tenant
    if (displayTenant) {
      crumbs.push({
        label: displayTenant,
        path: getTeamsV2PageUrl({ tenant: displayTenant }),
      });
    }

    // Add Realm if available - clickable to filter teams by tenant+realm
    if (displayRealmId) {
      crumbs.push({
        label: displayRealmId,
        path: getTeamsV2PageUrl({ tenant: displayTenant, realmId: displayRealmId }),
      });
    }

    // Add Team ID (not clickable from team page - we're already here)
    if (teamIdToUse) {
      crumbs.push({
        label: `Team ${teamIdToUse}`,
      });
    }

    // Add current view (no link - current page)
    crumbs.push({ label: viewLabel });

    return crumbs;
  }, [displayTenant, displayRealmId, teamIdToUse, viewLabel]);

  return (
    <React.Fragment>
      <Helmet title={`${teamIdToUse} | ${name}`} />
      <Box>
        <StandardPageHeader
          title={pageHeader}
          subtitle={isTeamDeactivated ? deactivateInfo : undefined}
          icon="Teams"
          customBreadcrumbs={customBreadcrumbs}
          titleSuffix={renderTeamStatusSuffix(teamStatus, teamData?.ssoEnabled)}
          sx={{ mb: 6 }}
        />
        {isTeamDeactivated ? (
          <Box mb={2}>
            <ChipsContainer>
              <Chip className="red-chip" label={deactivateInfo} />
            </ChipsContainer>
          </Box>
        ) : null}
        {warningMessage ? (
          <Box color={'red'} fontSize={'1rem'} paddingY={3} fontWeight={600}>
            {warningMessage}
          </Box>
        ) : (
          <></>
        )}
      </Box>
      <TeamOptionContainer>
        <IconContainer>
          <Button
            variant="outlined"
            color="primary"
            disabled={disableActionButton}
            onClick={(e) =>
              openTeamActionMenu({
                target: e.currentTarget,
                teamId: teamIdToUse,
                archivedAt: teamData?.archivedAt,
                scheduledArchiveDate: teamData?.scheduledArchiveDate,
                ddiEnabled: teamData?.ddiEnabled,
                // Unknown until teamData is this team's: the AI items stay disabled.
                aiEnabled: isTeamDataCurrent ? teamData?.aiEnabled : undefined,
                aiBlocked: isTeamDataCurrent ? teamData?.aiBlocked : undefined,
                teamName: isTeamDataCurrent ? teamData?.name : undefined,
              })
            }
          >
            Actions
          </Button>
        </IconContainer>
      </TeamOptionContainer>
      {TeamActionMenuComponent}

      <Divider sx={{ my: 6 }} />

      <Grid container spacing={6} sx={{ alignItems: 'stretch' }}>
        <Grid size={{ xs: 12, lg: 4, xl: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Grid style={devOnlyStyle(isDevMode)} size="auto">
              <Details />
            </Grid>
            <Grid style={devOnlyStyle(isDevMode)} size="auto">
              <Skills />
            </Grid>
            <Box sx={{ flexShrink: 0, mb: 6 }}>
              <TagsCard
                selectedTeam={selectedTeam}
                searchApiTags={teamData?.tags}
                isLoadingRailsApi={isFetchingTeams}
                isLoadingSearchApi={isLoadingTeamSearch}
              />
            </Box>
            <Box sx={{ flexShrink: 0, mb: 6 }}>
              <TeamAboutCard
                selectedTeam={selectedTeam}
                teamData={teamData}
                tenant={tenantToUse}
                realm={teamRealmToDisplay}
                realmType={teamRealm?.realm_type}
                teamId={teamIdToUse}
                internalContractorsCount={memberCategoryCounts?.internalContractor}
                isLoadingInternalContractors={isLoadingMemberCategoryCounts}
                isLoadingDetailedData={isFetchingTeams}
                isLoadingBasicData={isLoadingTeamSearch}
                onEnableDdi={
                  isTeamDataCurrent ? () => openEnableDdiModal({ teamId: teamIdToUse }) : undefined
                }
                isEnablingDdi={isEnablingDdi}
              />
            </Box>
            <IntegrationList
              handleIntegrationNameClick={handleIntegrationNameClick}
              onClickProvisionButton={onProvisionButtonClick}
              integrationHealthData={teamProfileIntegrationListIntegrationHealthData}
              teamId={teamIdToUse}
              tenant={tenantToUse}
              realmId={realmIdToUse}
              flexGrow
            />
            <Grid style={devOnlyStyle(isDevMode)} size="auto">
              <Elsewhere />
            </Grid>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, lg: 8, xl: 9 }}>
          <Grid size="auto">
            <TeamInfoSectionContainer
              teamId={teamIdToUse}
              isFetchingTeamMembers={isFetchingTeamMembers}
              isContextReady={isContextReady}
            />
          </Grid>
          <Grid container spacing={6}>
            <Grid style={devOnlyStyle(isDevMode)} size={{ xs: 12, lg: 4 }}>
              <Earnings />
            </Grid>
            <Grid style={devOnlyStyle(isDevMode)} size={{ xs: 12, lg: 4 }}>
              <Orders />
            </Grid>
            <Grid style={devOnlyStyle(isDevMode)} size={{ xs: 12, lg: 4 }}>
              <Revenue />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default Profile;
